import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, ExternalLink, Headphones, Rss, Star, Mic, Tag, ArrowRight, TrendingUp, Zap } from 'lucide-react';

import SEO from '../components/SEO';
import Section from '../components/v2/Section';
import V2Hero from '../components/v2/V2Hero';
import Reveal from '../components/v2/Reveal';
import { HERO } from '../data/heroImages';
import { CTABlock } from '../components/v2/CTAButton';
import { FEATURED, SUPPORTED } from '../data/clientPodcasts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * useLiveRss - hydrate a show with the latest episode + count from RSS.
 * Falls back silently to the static data when the feed is slow / offline.
 */
function useLiveRss(rssUrl) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!rssUrl) return;
    let cancelled = false;
    axios
      .get(`${API}/podcasts/parse-rss`, { params: { rss_url: rssUrl }, timeout: 6000 })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [rssUrl]);
  return data;
}

/**
 * Featured spotlight card - Katie Bell / Treat Your Business, full width.
 */
function SpotlightCard() {
  const live = useLiveRss(FEATURED.rssFeed);
  const episodeCount = live?.episode_count ?? '200+';
  const latestTitle = live?.episodes?.[0]?.title;

  return (
    <Reveal className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
      {/* Left: cover art with layered decoration */}
      <div className="lg:col-span-5 relative">
        <div className="absolute -inset-6 rounded-full bg-[#BC516F]/25 blur-3xl v2-blob pointer-events-none" aria-hidden="true" />
        <div className="relative aspect-square rounded-[22px] overflow-hidden border border-[#E2E8F0] shadow-[0_30px_70px_rgba(30,42,68,0.18)] v2-grain">
          <img
            src={FEATURED.coverArt}
            alt={`Cover art for the ${FEATURED.title} podcast hosted by ${FEATURED.host}, an Audio & Co. client show`}
            title={`${FEATURED.title} | Audio & Co. client podcast`}
            width="600"
            height="600"
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="spotlight-cover"
          />
        </div>
        <div className="absolute -bottom-4 -right-4 bg-[#1E2A44] text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg flex items-center gap-2">
          <Star className="w-3.5 h-3.5 text-[#BC516F]" fill="currentColor" />
          Full Pro Plan
        </div>
      </div>

      {/* Right: story */}
      <div className="lg:col-span-7">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E2E8F0] text-xs font-medium text-[#475569] mb-5">
          <Sparkles className="w-3.5 h-3.5 text-[#BC516F]" />
          Flagship client show
        </div>
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
          {FEATURED.title}
        </h2>
        <p className="text-[#475569] font-semibold mt-2 text-lg">
          Hosted by {FEATURED.host} <span className="text-[#94A3B8] font-normal">- {FEATURED.hostSub}</span>
        </p>
        <p className="text-[#475569] mt-5 leading-relaxed text-base sm:text-lg">
          {FEATURED.description}
        </p>

        {FEATURED.justShipped && (
          <div className="mt-4 inline-flex items-start gap-2 pl-3 pr-4 py-2 rounded-full bg-[#FCE2F3] border border-[#BC516F]/30 text-[#1E2A44]">
            <Zap className="w-4 h-4 text-[#BC516F] flex-shrink-0 mt-0.5" fill="currentColor" />
            <span className="text-sm font-semibold">{FEATURED.justShipped}</span>
          </div>
        )}

        {/* Wins strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-7">
          {FEATURED.wins.map((w) => (
            <div key={w.label} className="rounded-[12px] border border-[#E2E8F0] bg-white p-3 text-center">
              <div className="font-display font-bold text-[#1E2A44] text-xl sm:text-2xl leading-tight">{w.value}</div>
              <div className="text-[10px] text-[#64748B] uppercase tracking-widest font-semibold mt-1">{w.label}</div>
            </div>
          ))}
        </div>

        {latestTitle && (
          <div className="mt-5 rounded-[12px] bg-[#F6F8FB] border border-[#E2E8F0] p-4 flex items-start gap-3">
            <Mic className="w-4 h-4 text-[#BC516F] flex-shrink-0 mt-1" />
            <div className="text-sm">
              <div className="text-[#64748B] text-xs uppercase font-semibold tracking-widest">Latest episode ({episodeCount} shipped)</div>
              <div className="text-[#1E2A44] font-semibold mt-0.5">{latestTitle}</div>
            </div>
          </div>
        )}

        {FEATURED.quote && (
          <blockquote className="mt-6 border-l-4 border-[#BC516F] pl-4 italic text-[#1E2A44] text-base sm:text-lg leading-relaxed">
            &ldquo;{FEATURED.quote}&rdquo;
            <footer className="not-italic text-sm text-[#64748B] mt-2 font-medium">- {FEATURED.host}, host of {FEATURED.title}</footer>
          </blockquote>
        )}

        {/* Listen chips */}
        <div className="mt-6 flex flex-wrap gap-2">
          <ListenChip href={FEATURED.appleUrl} label="Apple Podcasts" />
          <ListenChip href={FEATURED.spotifyUrl} label="Spotify" />
          <ListenChip href={FEATURED.rssFeed} label="RSS" icon={<Rss className="w-3.5 h-3.5" />} />
          {FEATURED.websiteUrl && <ListenChip href={FEATURED.websiteUrl} label="Website" />}
        </div>

        {/* CTA to Pro Plan */}
        <div className="mt-7">
          <Link
            to="/podcast-visibility-package"
            className="inline-flex items-center gap-2 text-[#BC516F] font-semibold text-sm hover:gap-3 transition-all"
            data-testid="spotlight-see-pro-plan"
          >
            See what full Pro Plan production looks like <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </Reveal>
  );
}

function ListenChip({ href, label, icon }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-xs font-semibold text-[#1E2A44] hover:border-[#BC516F] hover:text-[#BC516F] transition"
    >
      {icon || <ExternalLink className="w-3.5 h-3.5" />}
      {label}
    </a>
  );
}

/**
 * Supported show card - grid tile. Live episode count injected when available.
 */
function ShowCard({ show, index }) {
  const live = useLiveRss(show.rssFeed);
  const episodeCount = live?.episode_count;
  const delayClass = `delay-${(index % 5) + 1}`;

  return (
    <Reveal className={delayClass}>
      <article
        className="group relative rounded-[16px] overflow-hidden bg-white border border-[#E2E8F0] hover:border-[#BC516F]/60 transition-all shadow-[0_8px_24px_rgba(30,42,68,0.06)] hover:shadow-[0_20px_50px_rgba(30,42,68,0.14)] hover:-translate-y-1 duration-300 will-change-transform"
        data-testid={`show-${show.slug}`}
      >
        <div className="relative aspect-square overflow-hidden">
          <img
            src={show.coverArt}
            alt={`Cover art for the ${show.title} podcast hosted by ${show.host}, an Audio & Co. client show`}
            title={`${show.title} | Audio & Co. client podcast`}
            width="600"
            height="600"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
          />
          {show.tag && (
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-[#1E2A44] text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
              <Tag className="w-2.5 h-2.5 text-[#BC516F]" />
              {show.tag}
            </div>
          )}
          {show.ranking && (
            <div className="absolute top-3 right-3 bg-[#BC516F] text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg">
              <TrendingUp className="w-2.5 h-2.5" />
              {show.ranking}
            </div>
          )}
          {episodeCount && (
            <div className="absolute bottom-3 right-3 bg-[#1E2A44]/90 backdrop-blur-sm text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full flex items-center gap-1.5">
              <Headphones className="w-2.5 h-2.5" />
              {episodeCount} eps
            </div>
          )}
        </div>
        <div className="p-5">
          <h3 className="font-display font-bold text-[#1E2A44] text-lg leading-tight tracking-tight">
            {show.title}
          </h3>
          {show.subtitle && (
            <p className="text-[#64748B] text-xs mt-1 italic">{show.subtitle}</p>
          )}
          <p className="text-[#64748B] text-xs uppercase tracking-widest font-semibold mt-2">
            {show.host}
          </p>
          <p className="text-[#475569] text-sm mt-3 line-clamp-3 leading-relaxed">
            {show.description}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {show.appleUrl && <ListenChip href={show.appleUrl} label="Apple" />}
            {show.spotifyUrl && <ListenChip href={show.spotifyUrl} label="Spotify" />}
            <ListenChip href={show.rssFeed} label="RSS" icon={<Rss className="w-3.5 h-3.5" />} />
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function Podcasts() {
  // Build a PodcastSeries JSON-LD collection for Google.
  const itemListLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Client podcasts produced by Audio and Co',
    itemListElement: [FEATURED, ...SUPPORTED].map((s, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'PodcastSeries',
        name: s.title,
        description: s.description,
        image: s.coverArt,
        author: { '@type': 'Person', name: s.host },
        webFeed: s.rssFeed,
      },
    })),
  };

  return (
    <main className="bg-white">
      <SEO
        title="Client Podcasts We Produce | Audio and Co"
        description="Client podcasts produced and managed by Audio & Co., from coaching and wellness shows to business podcasts ranking worldwide."
        url="https://audioandco.com/podcasts"
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListLd) }}
      />

      {/* HERO */}
      <V2Hero image={HERO.homePodcastDesk} testId="podcasts-hero">
        <div className="max-w-3xl">
          <Reveal className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E2E8F0] text-xs font-medium text-[#475569] mb-6 shadow-[0_4px_16px_rgba(30,42,68,0.06)]">
            <Headphones className="w-3.5 h-3.5 text-[#BC516F]" />
            A selection of client shows we love
          </Reveal>
          <Reveal delay={1} as="h1" className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
            A few of our favourite <span className="v2-shimmer">client shows</span>.
          </Reveal>
          <Reveal delay={2} as="p" className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">
            Every week we produce, edit and launch shows across health, business, spirituality and culture. Below is a hand-picked selection of client podcasts we are proud of &mdash; from a fully done-for-you Pro Plan flagship, to shows we launched from scratch, to long-runners we edit week in, week out.
          </Reveal>
          <Reveal delay={3} className="mt-8">
            <CTABlock size="lg" testIdPrefix="podcasts-hero-cta" />
          </Reveal>
        </div>
      </V2Hero>

      {/* SPOTLIGHT - Katie Bell */}
      <Section bg="white" testId="podcasts-spotlight">
        <div className="max-w-2xl mb-10">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">The full Pro Plan, live</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2 leading-[1.05]">
            This is what &ldquo;record once, we do the rest&rdquo; looks like.
          </h2>
          <p className="text-[#475569] mt-4 leading-relaxed">
            Katie is our flagship Pro Plan client. She records once a week. We produce every output. Two hundred episodes deep, five hundred clinic owners reached, still shipping every Wednesday.
          </p>
        </div>
        <SpotlightCard />
      </Section>

      {/* SUPPORTED SHOWS GRID */}
      <Section bg="cream" testId="podcasts-supported">
        <div className="max-w-2xl mb-10">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">More shows we&rsquo;re proud of &mdash; past and present</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2 leading-[1.05]">
            A selection from our past and present production family.
          </h2>
          <p className="text-[#475569] mt-4 leading-relaxed">
            A curated pick from across health, business, spirituality and culture &mdash; past and present shows we&rsquo;ve launched, produced or edited. Every cover below links straight to the live feed.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="supported-grid">
          {SUPPORTED.map((show, i) => (
            <ShowCard key={show.slug} show={show} index={i} />
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
            Want your show on this wall next quarter?
          </h2>
          <p className="text-white/75 mt-4 text-lg">
            We only take on a small number of Pro Plan clients each quarter. 30-minute call to see if you are one of them.
          </p>
          <div className="mt-7 flex justify-center">
            <CTABlock size="lg" align="center" invert testIdPrefix="podcasts-final-cta" />
          </div>
        </div>
      </Section>
    </main>
  );
}
